import { isNetworkError } from "ky";

export type FieldMap = Record<string, string | string[]>;

export type ServerError = {
  errors: Record<string, string[]>;
  codeMessage: string | null;
  serverMessage: string | null;
};

/**
 * Whether a request got no response at all, as when a phone loses signal.
 *
 * ky's isNetworkError misses this on iPhones in production: Sentry's fetch
 * wrapper gives Safari's "Load failed" a stack and appends the host, and ky only
 * trusts that message when it has no stack.
 */
export function isNetworkFailure(error: unknown): boolean {
  return (
    isNetworkError(error) ||
    (error instanceof TypeError && error.message.startsWith("Load failed"))
  );
}

/**
 * Friendly messages for the API's string error codes
 * (`{"error": "not_dismissible"}` and friends).
 */
const API_ERROR_MESSAGES: Record<string, string> = {
  not_dismissible: "This step can no longer be skipped.",
  invalid_reason: "That reason isn't valid. Pick another and try again.",
  not_reissuable: "This step can't be brought back.",
  not_found: "We couldn't find what you were looking for.",
  required_questions_missing: "Please answer every required question.",
  no_selection: 'Pick at least one vehicle, or choose "None of these".',
  not_removable:
    "Your dealer picked this one out for you, so it can't be removed here.",
  not_selectable: "This step can no longer be updated.",
  vehicle_not_found: "That vehicle is no longer available.",
};

/**
 * Reads the JSON body of a ky HTTPError. Returns null for non-HTTP errors
 * or unparseable bodies. The body stream can only be read once, so callers
 * that need more than one look at it should call this once and share.
 */
async function parseKyErrorBody(err: unknown): Promise<unknown> {
  if (!err || typeof err !== "object") return null;

  // ky consumes the response body to populate `error.data`, so `response.json()` on a
  // failed request throws "body already read" and every server message would be lost.
  // `data` is the supported way in and is pre-parsed for JSON content types.
  const { data } = err as { data?: unknown };
  if (data !== undefined && data !== null) return data;

  // Kept for anything that hands us a plain Response-carrying error rather than a ky one.
  const response = (err as { response?: Response }).response;
  if (response) {
    try {
      return await response.json();
    } catch {
      // fall through
    }
  }
  return null;
}

/**
 * Collects `{field: [messages]}` into `into`. A save of several rows nests each row's errors under
 * its index (`{"1": {amount: [...]}}`); those come out as dotted keys ("1.amount").
 */
function readErrorMessages(
  errors: Record<string, unknown>,
  prefix: string,
  into: Record<string, string[]>,
) {
  for (const [key, value] of Object.entries(errors)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      readErrorMessages(
        value as Record<string, unknown>,
        `${prefix}${key}.`,
        into,
      );
      continue;
    }
    const messages = (Array.isArray(value) ? value : []).filter(
      (m): m is string => typeof m === "string",
    );
    if (messages.length > 0) into[`${prefix}${key}`] = messages;
  }
}

/**
 * Reads an API error into its parts. The backend uses several error
 * shapes: `{errors: {field: [messages]}}` for validations, `{error: "code"}`
 * for task actions, and `{error: true, message: "..."}` for Plaid/session
 * failures. Never rejects: an unreadable error yields an empty ServerError.
 */
export async function readServerError(err: unknown): Promise<ServerError> {
  const result: ServerError = {
    errors: {},
    codeMessage: null,
    serverMessage: null,
  };
  const body = await parseKyErrorBody(err);
  if (!body || typeof body !== "object") return result;

  const { error, errors, message } = body as {
    error?: unknown;
    errors?: unknown;
    message?: unknown;
  };

  if (typeof error === "string") {
    result.codeMessage = Object.hasOwn(API_ERROR_MESSAGES, error)
      ? API_ERROR_MESSAGES[error]
      : null;
  }

  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    readErrorMessages(errors as Record<string, unknown>, "", result.errors);
  }

  if (typeof message === "string" && message) result.serverMessage = message;

  return result;
}

/** One display sentence for an API error. */
export function describeServerError(error: ServerError): string {
  if (error.codeMessage) return error.codeMessage;
  const messages = Object.values(error.errors).flat();
  if (messages.length > 0) return messages.join(", ");
  return error.serverMessage ?? "Something went wrong";
}

function humanize(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function asSentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/**
 * Splits an API error between the form's fields and the form itself.
 * `hasField` says which field names the form actually renders.
 */
export function routeServerError(
  error: ServerError,
  fieldMap: FieldMap,
  hasField: (name: string) => boolean,
): { fields: Record<string, string>; formError: string | null } {
  const fields: Record<string, string> = {};
  const pieces: string[] = [];

  for (const [serverKey, messages] of Object.entries(error.errors)) {
    const message = messages.join(", ");

    // Rails' :base errors belong to the record, not a field, and already read as
    // whole sentences ("Vandelay Autos is not accepting new applications."), so
    // they go out verbatim rather than picking up a humanized field-name prefix.
    if (serverKey === "base") {
      pieces.push(message);
      continue;
    }

    const mapped = fieldMap[serverKey];
    const targets = mapped
      ? Array.isArray(mapped)
        ? mapped
        : [mapped]
      : [serverKey];
    const accepted = targets.filter(hasField);
    for (const name of accepted) fields[name] = message;
    if (accepted.length === 0) pieces.push(`${humanize(serverKey)} ${message}`);
  }

  if (Object.keys(error.errors).length === 0) {
    return { fields, formError: describeServerError(error) };
  }
  return {
    fields,
    formError: pieces.length ? pieces.map(asSentence).join(" ") : null,
  };
}
